import dayjs from 'dayjs'

const norm = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .slice(0, 3)

const isActiveOnDate = (row, onDate) => {
  if (!row?.start_date || !row?.end_date) return false
  const day = dayjs(onDate || undefined).format('YYYY-MM-DD')
  const start = dayjs(row.start_date).format('YYYY-MM-DD')
  const end = dayjs(row.end_date).format('YYYY-MM-DD')
  return start <= day && day <= end
}

/**
 * Build a from|to → taux map. Partner rows override globals.
 * Also stores inverted reverse keys when only one direction exists.
 * Only rates active on `onDate` (check-in) are included.
 */
export const buildPartnerRateMap = (
  partnerRates = [],
  globalRates = [],
  tndToPartner = null,
  onDate = null
) => {
  const map = new Map()

  const put = (from, to, taux, { allowOverride = true } = {}) => {
    const f = norm(from)
    const t = norm(to)
    const n = Number(taux)
    if (!f || !t || f === t || !Number.isFinite(n) || n <= 0) return
    const key = `${f}|${t}`
    if (!allowOverride && map.has(key)) return
    map.set(key, n)
  }

  for (const row of globalRates || []) {
    if (!isActiveOnDate(row, onDate)) continue
    put(row.devise_from, row.devise_to, row.taux, { allowOverride: false })
  }
  for (const row of partnerRates || []) {
    if (!isActiveOnDate(row, onDate)) continue
    put(row.devise_from, row.devise_to, row.taux, { allowOverride: true })
  }

  // Seed only when check-in window had no partner/global TND→partner row
  if (tndToPartner?.currency && tndToPartner?.taux != null) {
    put('TND', tndToPartner.currency, tndToPartner.taux, { allowOverride: false })
  }

  for (const [key, taux] of [...map.entries()]) {
    const [from, to] = key.split('|')
    const rev = `${to}|${from}`
    if (!map.has(rev) && taux > 0) map.set(rev, 1 / taux)
  }

  return map
}

/**
 * Convert amount from → to using rate map (direct, reverse, or via TND).
 * Returns null when no rate path exists (caller must keep source currency).
 */
export const convertWithRateMap = (amount, fromCurrency, toCurrency, rateMap) => {
  const value = Number(amount)
  if (!Number.isFinite(value)) return null
  const from = norm(fromCurrency)
  const to = norm(toCurrency)
  if (!from || !to) return null
  if (from === to) return Number(value.toFixed(2))

  const direct = rateMap?.get?.(`${from}|${to}`)
  if (direct != null) return Number((value * direct).toFixed(2))

  if (from !== 'TND' && to !== 'TND') {
    const toTnd = rateMap?.get?.(`${from}|TND`)
    const fromTnd = rateMap?.get?.(`TND|${to}`)
    if (toTnd != null && fromTnd != null) {
      return Number((value * toTnd * fromTnd).toFixed(2))
    }
  }

  return null
}

export const resolveHotelCurrency = (hotel = {}) =>
  norm(
    hotel.partnerFx?.from ||
      hotel.currency ||
      hotel.devise ||
      hotel.displayCurrency ||
      hotel.providerOffer?.currency
  )

/**
 * True when a dated TND → partner.currency rate exists
 * (partner specification seed or rateMap from partner → global).
 */
export const hasTndToPartnerRate = (partnerDisplay) => {
  if (!partnerDisplay) return false
  const currency = norm(partnerDisplay.currency)
  if (!currency || currency === 'TND') return false
  const seed = Number(partnerDisplay.taux)
  if (Number.isFinite(seed) && seed > 0) return true
  const mapped = Number(partnerDisplay.rateMap?.get?.(`TND|${currency}`))
  return Number.isFinite(mapped) && mapped > 0
}

/**
 * Currency to show on cards.
 * Partner mode: partner devise when TND→partner rate exists; else TND.
 * TND mode / no partner display: TND or hotel source.
 * Local hotels with explicit partnerFx keep that path.
 */
export const resolveHotelDisplayCurrency = (
  hotel,
  partnerDisplay,
  { showAsTnd = false } = {}
) => {
  const source = resolveHotelCurrency(hotel)
  if (!partnerDisplay) {
    return hotel?.displayCurrency || source || ''
  }
  if (showAsTnd) return 'TND'

  const fxTaux = Number(hotel?.partnerFx?.taux)
  const hasConvertibleFx = Number.isFinite(fxTaux) && fxTaux > 0

  if (hasConvertibleFx) {
    return partnerDisplay.currency || hotel?.partnerFx?.currency || source
  }

  // Local hotel explicitly had no check-in rate → keep source currency
  if (hotel?.partnerFx != null && !hasConvertibleFx) {
    return hotel?.displayCurrency || source || ''
  }

  // TGX / TND display pipeline: need TND → partner.currency (spec then global)
  if (hasTndToPartnerRate(partnerDisplay)) {
    return partnerDisplay.currency
  }

  return 'TND'
}

/**
 * Convert using hotel.partnerFx first (partner → contract period → global from API).
 * TGX amounts are already in display currency (usually TND): TND mode keeps them;
 * partner mode applies TND → partner via partner taux / rateMap.
 * If partnerFx.taux is null/missing on a local hotel with an FX row → keep source amount.
 */
export const convertHotelAmountForPartner = (
  amount,
  hotel,
  partnerDisplay,
  { toTnd = false } = {}
) => {
  const value = Number(amount)
  if (!Number.isFinite(value)) return 0
  if (!partnerDisplay) return Number(value.toFixed(2))

  const from = resolveHotelCurrency(hotel)
  const partnerCurrency = norm(partnerDisplay.currency)
  const fx = hotel?.partnerFx
  const fxTaux = Number(fx?.taux)
  const fxFrom = norm(fx?.from || '')
  const fxTo = norm(fx?.to || fx?.currency || '')
  const hasFxRow = fx != null
  const hasConvertibleFx = Number.isFinite(fxTaux) && fxTaux > 0

  // Already in the requested display currency
  if (toTnd && from === 'TND') return Number(value.toFixed(2))
  if (!toTnd && from && from === partnerCurrency) return Number(value.toFixed(2))

  // Search explicitly found no rate for check-in → keep source amount
  if (hasFxRow && !hasConvertibleFx) {
    return Number(value.toFixed(2))
  }

  // Display as TND: reverse partner FX when amount is already in partner currency
  if (toTnd) {
    if (from === partnerCurrency && typeof partnerDisplay.displayToTnd === 'function') {
      const asTnd = partnerDisplay.displayToTnd(value)
      if (asTnd == null || !Number.isFinite(Number(asTnd))) {
        return Number(value.toFixed(2))
      }
      return Number(Number(asTnd).toFixed(2))
    }
    const asTnd = convertWithRateMap(value, from, 'TND', partnerDisplay.rateMap)
    if (asTnd == null) return Number(value.toFixed(2))
    return asTnd
  }

  // Display as partner currency: incoming (often already TND) → partner devise
  // Rate order is encoded in rateMap: partner specification overrides global.
  let inPartner = value
  if (
    hasConvertibleFx &&
    fxFrom &&
    fxFrom === from &&
    (!fxTo || fxTo === partnerCurrency || fxTo === from)
  ) {
    inPartner = value * fxTaux
  } else if (from === 'TND') {
    if (!hasTndToPartnerRate(partnerDisplay)) {
      return Number(value.toFixed(2))
    }
    const converted =
      typeof partnerDisplay.convertTnd === 'function'
        ? partnerDisplay.convertTnd(value)
        : convertWithRateMap(value, 'TND', partnerCurrency, partnerDisplay.rateMap)
    if (converted == null || !Number.isFinite(Number(converted))) {
      return Number(value.toFixed(2))
    }
    inPartner = Number(converted)
  } else if (typeof partnerDisplay.convertFrom === 'function') {
    const converted = partnerDisplay.convertFrom(amount, from)
    if (converted == null || !Number.isFinite(Number(converted))) {
      return Number(value.toFixed(2))
    }
    inPartner = Number(converted)
  } else {
    const converted = convertWithRateMap(
      value,
      from,
      partnerCurrency,
      partnerDisplay.rateMap
    )
    if (converted == null) return Number(value.toFixed(2))
    inPartner = converted
  }

  return Number(Number(inPartner).toFixed(2))
}
